import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { capsulesApi, Capsule, ContentItem } from '@/services/capsulesApi';

function pickImageFromContent(content: ContentItem[] | undefined): string {
  if (!content || content.length === 0) return '';
  const imageItem = content.find(c => c.type === 'image' && Boolean(c.url));
  return imageItem?.previewUrl || imageItem?.url || '';
}

export function useInfiniteCapsulesFeed(params: {
  userId: string;
  pageSize?: number;
}) {
  const { userId, pageSize = 20 } = params;
  const [page, setPage] = useState(1);
  const [items, setItems] = useState<Capsule[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const loadInFlightRef = useRef(false);

  const loadPage = useCallback(
    async (pageToLoad: number) => {
      if (loadInFlightRef.current || !hasMore) return;
      loadInFlightRef.current = true;
      setIsLoading(true);
      try {
        const res = await capsulesApi.getCapsules({
          page: pageToLoad,
          limit: pageSize,
          isOpened: true,
        });
        const list = res.data.capsules || [];
        const filtered = list.filter(c => c.meta?.isOpened === true);
        setItems(prev => {
          const base = pageToLoad === 1 ? [] : prev;
          const merged = [...base, ...filtered];
          const seen = new Set<string>();
          const deduped: Capsule[] = [];
          for (const c of merged) {
            if (seen.has(c.id)) continue;
            seen.add(c.id);
            deduped.push(c);
          }
          deduped.sort((a, b) => {
            const ad = a.meta.scheduledAt || a.meta.createdAt;
            const bd = b.meta.scheduledAt || b.meta.createdAt;
            return new Date(bd).getTime() - new Date(ad).getTime();
          });
          return deduped;
        });
        setHasMore(res.data.hasMore);
        setPage(pageToLoad + 1);
      } catch {
        setHasMore(false);
        if (pageToLoad === 1) {
          setItems([]);
        }
      } finally {
        loadInFlightRef.current = false;
        setIsLoading(false);
      }
    },
    [hasMore, pageSize]
  );

  useEffect(() => {
    loadInFlightRef.current = false;
    setIsLoading(false);
    setItems([]);
    setHasMore(true);
    setPage(1);
  }, [userId, pageSize]);

  useEffect(() => {
    if (page === 1 && hasMore && !loadInFlightRef.current && !isLoading) {
      void loadPage(1);
    }
  }, [page, hasMore, isLoading, loadPage]);

  const cards = useMemo(
    () =>
      items.map(c => {
        const coauthorAvatars = (c.privacy.coauthors || [])
          .map(a => a.avatar)
          .filter(Boolean) as string[];
        const avatars = [c.author.avatar || '', ...coauthorAvatars].filter(v =>
          Boolean(v)
        );
        const coauthorsCount =
          c.privacy.coauthors?.length || c.privacy.allowedUserIds?.length || 0;
        return {
          id: c.id,
          title: c.title,
          image:
            pickImageFromContent(c.content) || c.imagePreview || c.image || '',
          ownerId: c.author.id,
          ownerName: c.author.name,
          avatars,
          coauthors: coauthorsCount,
          commentsCount: c.engagement?.comments ?? 0,
          isMyCapsule: c.author.id === userId,
          isAdminCapsule: Boolean(c.author.isAdministrator),
        };
      }),
    [items, userId]
  );

  const loadMore = useCallback(() => {
    if (loadInFlightRef.current || !hasMore) return;
    void loadPage(page);
  }, [hasMore, loadPage, page]);

  return { items, cards, isLoading, hasMore, loadMore };
}
